/** Small shared helpers that behave identically on web, desktop, mobile and server. */

export function initialsFor(name: string): string {
  const parts = name
    .replace(/[^\p{L}\p{N} '-]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]!.charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : parts[0]!.charAt(1);
  return (first + (last ?? '')).toUpperCase();
}

/** RFC 4122 v4 UUID using the platform CSPRNG. */
interface PlatformCrypto {
  randomUUID?: () => string;
  getRandomValues?: (array: Uint8Array) => Uint8Array;
}

export function newId(): string {
  const c = (globalThis as { crypto?: PlatformCrypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  if (!c || typeof c.getRandomValues !== 'function') {
    throw new Error('A secure random source is required');
  }
  const b = c.getRandomValues(new Uint8Array(16));
  const hex = (x: number) => x.toString(16).padStart(2, '0');
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, hex).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Client retry key for chat messages. */
export function newClientKey(): string {
  return `ck_${newId()}`;
}

const MENTION = /(^|\s)@([\p{L}\p{N}._-]{1,64})/gu;

/** Extracts @handles from a chat body. Resolution to users happens on the server. */
export function extractMentionHandles(body: string): string[] {
  const out = new Set<string>();
  for (const m of body.matchAll(MENTION)) out.add(m[2]!.toLowerCase());
  return [...out];
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

export function fileKindLabel(mime: string, name: string): string {
  const ext = name.includes('.') ? name.split('.').pop()!.toUpperCase() : '';
  if (mime === 'application/pdf') return 'PDF document';
  if (mime.startsWith('image/')) return `${ext || 'Image'} image`;
  if (mime.startsWith('video/')) return 'Video';
  if (mime.startsWith('audio/')) return 'Audio';
  if (mime.includes('spreadsheet') || ext === 'XLSX' || ext === 'CSV') return 'Spreadsheet';
  if (mime.includes('presentation') || ext === 'PPTX') return 'Presentation';
  if (mime.includes('word') || ext === 'DOCX') return 'Document';
  if (mime.startsWith('text/')) return 'Text file';
  return ext ? `${ext} file` : 'File';
}
